import { Injectable } from '@angular/core';

export interface FieldDefinition {
  key: string;
  label: string;
  description: string;
  section: string;
}

@Injectable({
  providedIn: 'root'
})
export class ReportFieldDefinitionsService {

  private readonly STORAGE_KEY = 'causable_show_report_help';

  private fieldDefinitions: FieldDefinition[] = [
    // Game Details Section
    {
      key: 'licenceNumber',
      label: 'License #',
      description: 'The lottery license number issued by the gaming regulatory authority that authorizes this draw. Required for legal compliance and audit purposes.',
      section: 'Game Details'
    },
    {
      key: 'endDate',
      label: 'Game End Date',
      description: 'The date and time when ticket sales closed for this draw period. No purchases can be made after this time.',
      section: 'Game Details'
    },
    {
      key: 'drawTime',
      label: 'Draw Time',
      description: 'The exact date and time when the winning number was randomly selected. This is the official timestamp of the draw event.',
      section: 'Game Details'
    },
    {
      key: 'winningNumber',
      label: 'Drawn #',
      description: 'The ticket number that was randomly selected as the winning number for this draw.',
      section: 'Game Details'
    },
    {
      key: 'winnerName',
      label: 'Drawn Person',
      description: 'The name of the player who owns the drawn ticket number. Note: This may be hidden for privacy if the player did not actually play their ticket (rollover scenario).',
      section: 'Game Details'
    },
    {
      key: 'voidedTickets',
      label: 'Voided Tickets',
      description: 'Tickets that were refunded or cancelled during this draw period. These tickets were not eligible for the draw.',
      section: 'Game Details'
    },
    {
      key: 'playedTickets',
      label: 'Played Tickets',
      description: 'Tickets that were actively purchased and entered into this draw. These are the tickets eligible to win.',
      section: 'Game Details'
    },
    {
      key: 'notPlayedTickets',
      label: 'Not Played Tickets',
      description: 'Tickets owned by players who did not purchase entry for this draw period. For GoldRush, these are strips that exist but were not played this round.',
      section: 'Game Details'
    },
    {
      key: 'totalTickets',
      label: 'Total Tickets',
      description: 'The sum of voided, played, and not played tickets - represents all tickets associated with this draw period.',
      section: 'Game Details'
    },

    // Refund Details Section
    {
      key: 'numberOfRefundedPayments',
      label: 'Number of Refunds Issued',
      description: 'The count of separate refund transactions processed during this draw period (one player may have multiple refunds).',
      section: 'Refund Details'
    },
    {
      key: 'numberOfRefundedTickets',
      label: 'Number of Refunded Tickets',
      description: 'The total count of individual tickets that were refunded across all refund transactions.',
      section: 'Refund Details'
    },
    {
      key: 'totalRefundsDollarAmount',
      label: 'Total Refund Dollar Amount',
      description: 'The total monetary value returned to players through refunds during this draw period.',
      section: 'Refund Details'
    },
    {
      key: 'totalRefundsCharityContribution',
      label: 'Charity Refund Fees',
      description: 'The portion of refunded amounts that would have gone to charities. This reduces the net charity contribution for the draw.',
      section: 'Refund Details'
    },

    // Dispute Details Section
    {
      key: 'numberOfDisputedPayments',
      label: 'Number of Disputes',
      description: 'The count of payment disputes (chargebacks) filed by players\' banks during this draw period.',
      section: 'Dispute Details'
    },
    {
      key: 'totalDisputesDollarAmount',
      label: 'Total Dispute Dollar Amount',
      description: 'The total monetary value of all disputed payments during this draw period.',
      section: 'Dispute Details'
    },
    {
      key: 'totalStripeDisputeFees',
      label: 'Stripe Dispute Fees',
      description: 'Fees charged by Stripe (payment processor) for handling disputes typically $15 per dispute. This is half of that value',
      section: 'Dispute Details'
    },
    {
      key: 'charityDisputeFees',
      label: 'Charity Dispute Fees',
      description: 'The combined cost to charities from disputes, including their share of the disputed amount plus any dispute-related fees allocated to them.',
      section: 'Dispute Details'
    },

    // Payout Details Section
    {
      key: 'winnerPlayed',
      label: 'Winning Number Played',
      description: '(GoldRush only) Whether the person whose number was drawn actually purchased/played their ticket this round. If "No", the prize rolls over to the next draw.',
      section: 'Payout Details'
    },
    {
      key: 'winnerAmount',
      label: 'Winner Payout',
      description: 'The prize amount awarded to the winner - 50% of the total pot. If the winner did not play, this shows what would have been won.',
      section: 'Payout Details'
    },
    {
      key: 'winnerPayoutStatus',
      label: 'Winner Payout Status',
      description: 'The current status of the prize claim: Not Paid, Paid, Forfeited, etc. Only shown when the winner actually played their ticket.',
      section: 'Payout Details'
    },

    // Pot Total Breakdown Section
    {
      key: 'seedTotal',
      label: 'Seed Contribution to Pot',
      description: 'Any promotional or organizational money added to boost the prize pool. Manually added by administrators.',
      section: 'Pot Total Breakdown'
    },
    {
      key: 'previousPotTotal',
      label: 'Rollover Contribution',
      description: '(GoldRush only) The pot amount carried over from the previous draw when no one won. In 50/50 games, this is always zero as each draw stands alone.',
      section: 'Pot Total Breakdown'
    },
    {
      key: 'grossSales',
      label: 'Gross Sales',
      description: 'The total amount collected from all ticket purchases before any fees or deductions.',
      section: 'Pot Total Breakdown'
    },
    {
      key: 'manualSales',
      label: 'Manual Sales',
      description: 'Portion of gross sales from players who manually purchased tickets during this draw period.',
      section: 'Pot Total Breakdown'
    },
    {
      key: 'autoplaySales',
      label: 'Autoplay Sales',
      description: 'Portion of gross sales from players enrolled in autoplay who were automatically charged for this draw.',
      section: 'Pot Total Breakdown'
    },
    {
      key: 'disputeFees',
      label: 'Dispute Fees',
      description: 'Total fees incurred from payment disputes, shown as a deduction from the pot calculation.',
      section: 'Pot Total Breakdown'
    },
    {
      key: 'potTotal',
      label: 'Pot Total',
      description: 'The final prize pool amount. Calculated as: Seed + Rollover + (Gross Sales / 2) - Dispute Fees. The winner receives 50% of this.',
      section: 'Pot Total Breakdown'
    },

    // Fee Breakdown Section
    {
      key: 'totalCausableFees',
      label: 'Causable Fees',
      description: 'Platform fees charged by Causable for operating the lottery system. Typically a percentage of each ticket sale.',
      section: 'Fee Breakdown'
    },
    {
      key: 'hstOnCausableFees',
      label: 'HST on Causable Fees',
      description: 'Harmonized Sales Tax (13-15% depending on province) applied to Causable\'s platform fees. Shown for Ontario, Nova Scotia, New Brunswick, Newfoundland, and PEI.',
      section: 'Fee Breakdown'
    },
    {
      key: 'gstOnCausableFees',
      label: 'GST on Causable Fees',
      description: 'Federal Goods and Services Tax (5%) applied to Causable\'s platform fees. Shown for provinces with separate GST/PST or GST only (Alberta, BC, Manitoba, Saskatchewan, Territories).',
      section: 'Fee Breakdown'
    },
    {
      key: 'pstOnCausableFees',
      label: 'PST/QST on Causable Fees',
      description: 'Provincial Sales Tax applied to Causable\'s platform fees. Shown for BC (7%), Manitoba (7%), and Saskatchewan (6%). For Quebec, this displays as QST (9.975%).',
      section: 'Fee Breakdown'
    },
    {
      key: 'totalStripeFees',
      label: 'Stripe Fees',
      description: 'Payment processing fees charged by Stripe for credit card transactions. Typically ~2.9% + $0.30 per transaction.',
      section: 'Fee Breakdown'
    },
    {
      key: 'refundFees',
      label: 'Refund Fees',
      description: 'The charity\'s portion of amounts that were refunded. This represents lost revenue to charities from refunded tickets.',
      section: 'Fee Breakdown'
    },
    {
      key: 'stripeDisputeFeesBreakdown',
      label: 'Stripe Dispute Fees',
      description: 'Per-dispute fees from Stripe for processing chargebacks (typically $15 each).',
      section: 'Fee Breakdown'
    },
    {
      key: 'charityDisputeFeesBreakdown',
      label: 'Charity Dispute Fees',
      description: 'The charity\'s share of dispute-related costs, including disputed amounts and allocated fees.',
      section: 'Fee Breakdown'
    },
    {
      key: 'totalAdministrationCommissionFees',
      label: 'Charity Host Admin Fees',
      description: 'Administrative commission paid to the organization hosting/managing the lottery on behalf of charities.',
      section: 'Fee Breakdown'
    },
    {
      key: 'totalFees',
      label: 'Total Fees',
      description: 'Sum of all fees. This amount is deducted from gross sales before calculating charity proceeds.',
      section: 'Fee Breakdown'
    },

    // Charity Revenue Breakdown Section
    {
      key: 'charityGrossSales',
      label: 'Gross Sales',
      description: 'Total ticket sales before any deductions. Starting point for charity revenue calculation.',
      section: 'Charity Revenue Breakdown'
    },
    {
      key: 'prizeAmount',
      label: 'Prize Amount',
      description: 'The portion of sales allocated to the prize pool (50% of net sales). Deducted from charity\'s share.',
      section: 'Charity Revenue Breakdown'
    },
    {
      key: 'charityTotalFees',
      label: 'Total Fees',
      description: 'All operating costs (platform, payment processing, taxes, disputes). Deducted from charity\'s share.',
      section: 'Charity Revenue Breakdown'
    },
    {
      key: 'netProceedsForCharity',
      label: 'Net Proceeds For Charity',
      description: 'The final amount distributed to charities. Formula: Gross Sales - Prize Amount - Total Fees - Refunds - Disputes.',
      section: 'Charity Revenue Breakdown'
    },

    // Charity Breakdown Grid
    {
      key: 'charityName',
      label: 'Group',
      description: 'The name of the charity receiving funds from this draw.',
      section: 'Charity Breakdown Grid'
    },
    {
      key: 'charityGross',
      label: 'Gross',
      description: 'The charity\'s share of proceeds before any refunds or disputes from their supporters.',
      section: 'Charity Breakdown Grid'
    },
    {
      key: 'charityRefundFees',
      label: 'Refund Fees',
      description: 'Deductions from this charity due to refunds from their supporters.',
      section: 'Charity Breakdown Grid'
    },
    {
      key: 'charityDisputeFeesGrid',
      label: 'Dispute Fees',
      description: 'Deductions from this charity due to disputes from their supporters.',
      section: 'Charity Breakdown Grid'
    },
    {
      key: 'charityNet',
      label: 'Net',
      description: 'Final amount this charity receives: Gross - Refund Fees - Dispute Fees.',
      section: 'Charity Breakdown Grid'
    },
    {
      key: 'numberOfPlayers',
      label: 'Number of Players',
      description: 'Count of unique players who supported this charity during this draw.',
      section: 'Charity Breakdown Grid'
    },

    // Comprehensive Report - Sales Breakdown Section
    {
      key: 'comprehensiveGrossSales',
      label: 'Gross Sales',
      description: 'The total amount collected from all ticket purchases across the selected date range, before any fees or deductions are applied.',
      section: 'Comprehensive Report - Sales Breakdown'
    },
    {
      key: 'comprehensiveDisputeFees',
      label: 'Dispute Fees',
      description: 'Total fees incurred from payment disputes (chargebacks) during the date range. Includes both Stripe dispute fees and the charity\'s share of disputed amounts.',
      section: 'Comprehensive Report - Sales Breakdown'
    },
    {
      key: 'comprehensiveAdminFees',
      label: 'Charity Host Admin Fees',
      description: 'Administrative commission paid to the organization hosting/managing the lottery on behalf of charities during the date range.',
      section: 'Comprehensive Report - Sales Breakdown'
    },
    {
      key: 'comprehensiveCausableFees',
      label: 'Causable Fees',
      description: 'Platform fees charged by Causable for operating the lottery system during the date range. Typically a percentage of each ticket sale.',
      section: 'Comprehensive Report - Sales Breakdown'
    },
    {
      key: 'comprehensiveHstOnCausable',
      label: 'HST on Causable Fees',
      description: 'Harmonized Sales Tax (13-15% depending on province) applied to Causable\'s platform fees during the date range. Shown for Ontario, Nova Scotia, New Brunswick, Newfoundland, and PEI.',
      section: 'Comprehensive Report - Sales Breakdown'
    },
    {
      key: 'comprehensiveGstOnCausable',
      label: 'GST on Causable Fees',
      description: 'Federal Goods and Services Tax (5%) applied to Causable\'s platform fees during the date range. Shown for provinces with separate GST/PST or GST only.',
      section: 'Comprehensive Report - Sales Breakdown'
    },
    {
      key: 'comprehensivePstOnCausable',
      label: 'PST/QST on Causable Fees',
      description: 'Provincial Sales Tax applied to Causable\'s platform fees during the date range. Shown for BC (7%), Manitoba (7%), and Saskatchewan (6%). For Quebec, this displays as QST (9.975%).',
      section: 'Comprehensive Report - Sales Breakdown'
    },
    {
      key: 'comprehensiveStripeFees',
      label: 'Stripe Fees',
      description: 'Payment processing fees charged by Stripe for credit card transactions during the date range. Typically ~2.9% + $0.30 per transaction.',
      section: 'Comprehensive Report - Sales Breakdown'
    },
    {
      key: 'comprehensiveCharityRefundFees',
      label: 'Charity Refund Fees',
      description: 'The portion of refunded amounts that would have gone to charities during the date range. This reduces the net charity contribution.',
      section: 'Comprehensive Report - Sales Breakdown'
    },
    {
      key: 'comprehensiveCharityDisputeFees',
      label: 'Charity Dispute Fees',
      description: 'The combined cost to charities from disputes during the date range, including their share of the disputed amount plus any dispute-related fees.',
      section: 'Comprehensive Report - Sales Breakdown'
    },
    {
      key: 'comprehensivePrizeAmount',
      label: 'Prize Amount',
      description: 'The total portion of sales allocated to prize pools (50% of net sales) across all draws in the date range.',
      section: 'Comprehensive Report - Sales Breakdown'
    },
    {
      key: 'comprehensiveNetProceedsForCharity',
      label: 'Net Proceeds For Charity',
      description: 'The final total amount distributed to charities across the date range. Formula: Gross Sales - Prize Amount - Total Fees - Refunds - Disputes.',
      section: 'Comprehensive Report - Sales Breakdown'
    },

    // Comprehensive Report - Game Counts Section
    {
      key: 'drawsWithNoWinner',
      label: 'Draws with no Winner',
      description: 'The number of draws during the date range where the drawn ticket was not played (GoldRush rollover scenario) or no winner was determined.',
      section: 'Comprehensive Report - Game Counts'
    },
    {
      key: 'drawsWithWinner',
      label: 'Draws with a winner',
      description: 'The number of draws during the date range where a winner was successfully determined and the prize was awarded.',
      section: 'Comprehensive Report - Game Counts'
    },
    {
      key: 'totalDraws',
      label: 'Total Number of Draws',
      description: 'The total count of completed draws that occurred within the selected date range.',
      section: 'Comprehensive Report - Game Counts'
    },

    // Comprehensive Report - Player Numbers Section
    {
      key: 'totalNumbersPlayed',
      label: 'Total numbers played',
      description: 'The total count of tickets/numbers that were actively purchased and entered into draws during the date range.',
      section: 'Comprehensive Report - Player Numbers'
    },
    {
      key: 'totalUnplayedNumbers',
      label: 'Total unplayed numbers',
      description: 'The total count of tickets/numbers owned by players that were not purchased for draws during the date range (GoldRush strips not played).',
      section: 'Comprehensive Report - Player Numbers'
    },

    // Comprehensive Report - Players Section
    {
      key: 'playersPlayed',
      label: 'Played',
      description: 'The count of unique players who made at least one purchase and participated in draws during the date range.',
      section: 'Comprehensive Report - Players'
    },
    {
      key: 'playersNotPlayed',
      label: 'Not Played',
      description: 'The count of players who have tickets/numbers but did not make any purchases during the date range.',
      section: 'Comprehensive Report - Players'
    },

    // Comprehensive Report - Group Breakdown Section
    {
      key: 'totalCharityContribution',
      label: 'Total Charity Contribution',
      description: 'The combined net proceeds distributed across all charities during the date range, after all fees, refunds, and disputes are deducted.',
      section: 'Comprehensive Report - Group Breakdown'
    }
  ];

  getDefinition(key: string): FieldDefinition | undefined {
    return this.fieldDefinitions.find(def => def.key === key);
  }

  getTooltip(key: string): string {
    const definition = this.getDefinition(key);
    return definition?.description || '';
  }

  getAllDefinitions(): FieldDefinition[] {
    return this.fieldDefinitions;
  }

  getDefinitionsBySection(section: string): FieldDefinition[] {
    return this.fieldDefinitions.filter(def => def.section === section);
  }

  getSections(): string[] {
    return [...new Set(this.fieldDefinitions.map(def => def.section))];
  }

  /**
   * Returns a pre-computed map of field keys to descriptions.
   * Use this in components instead of calling getTooltip() in templates.
   */
  getDescriptionsMap(): Record<string, string> {
    return this.fieldDefinitions.reduce((map, def) => {
      map[def.key] = def.description;
      return map;
    }, {} as Record<string, string>);
  }

  getShowHelpPreference(): boolean {
    const stored = localStorage.getItem(this.STORAGE_KEY);
    return stored === 'true';
  }

  setShowHelpPreference(show: boolean): void {
    localStorage.setItem(this.STORAGE_KEY, show.toString());
  }
}
